import { Prop, Vue, Watch } from 'vue-property-decorator';
import Utils from '@/assets/js/utils/Utils';
import type { WidgetConfig } from '@/modules/ctx-dashboard';

import type { DataResolver } from '@/components/widgets/data/DataResolver';
import { Resolution } from '@/modules/shared/types';
import { i18n, I18nAdapter, I18nChangedEventType } from '@/plugins/i18n';
import { WidgetError } from '@/components/widgets/types/WidgetError';
import { WidgetErrorType } from '@/components/widgets/types/WidgetErrorType';
import { ref, computed, watch, onMounted, onBeforeMount, onBeforeUnmount, Ref } from 'vue';
import type { I18nChangedEvent } from '@/plugins/i18n';
import { MenuAction } from '@/components/widgets/types/MenuAction';

export interface WidgetSettings<D = any> {
    supportsCompare: boolean;
    supportsTable: boolean;
    enableTimeBar: boolean;
    enabledDataStreaming: boolean;
    dataResolver?: DataResolver<D>;
}

export class Widget<D = any> extends Vue {

    @Prop({ default: () => ({}) })
    public readonly widgetConfig!: WidgetConfig;

    @Prop({ default: false })
    public readonly expanded!: boolean;

    @Prop({ default: false })
    public readonly report!: boolean;

    @Prop({ default: false })
    public readonly loading!: boolean;

    protected renderWidget: boolean = true;
    private loadDataQueued: boolean = true;
    private pendingLoadData: Promise<void>|undefined = undefined;
    private readonly onI18nChanged = (event: I18nChangedEvent): void => {
        if (event.type === I18nChangedEventType.LanguageLoaded && event.lang === i18n.locale) {
            this.loadWidget();
        }
    };

    public static defaultSettings: WidgetSettings<any> = {
        supportsCompare: false,
        supportsTable: false,
        enableTimeBar: true,
        enabledDataStreaming: false,
        dataResolver: undefined,
    };

    public settings: WidgetSettings<D>;

    constructor(settings?: Partial<WidgetSettings<D>>) {
        super();
        this.settings = Utils.deepCopy(Widget.defaultSettings);
        if (settings) {
            Object.assign(this.settings, settings);
        }
    }

    public created(): void {
        I18nAdapter.onChange(this.onI18nChanged);
        // reload 10min widgets every 15 minutes
        if (!this.report && this.widget.resolution === Resolution.Min10) {
            setInterval(this.loadData, 1000 * 60 * 15);
        }
    }

    public mounted(): void {
        this.$emit('update:settings', this.settings);
        this.loadWidget();
    }

    public beforeDestroy(): void {
        I18nAdapter.offChange(this.onI18nChanged);
    }

    @Watch('expanded')
    private onExpanded(): void {
        this.renderWidget = false;
        this.$nextTick(() => this.renderWidget = true);
    }

    @Watch('widget')
    private onWidgetChanged(): void {
        this.loadWidget();
    }

    @Watch('$i18n.locale')
    private onLanguageChanged(): void {
        this.loadWidget();
    }

    protected get widget(): WidgetConfig {
        return Utils.deepCopy(this.widgetConfig);
    }

    public async loadWidget(): Promise<void> {
        this.loadDataQueued = true;
        if (this.pendingLoadData) {
            await this.pendingLoadData;
        }
        this.pendingLoadData = (async () => {
            this.loadDataQueued = false;
            this.$emit('update:loading', true);
            await this.loadData(); // Ensure all processing completes here
            if (!this.loadDataQueued) {
                await this.$nextTick(); // Await Vue rendering
                this.$emit('update:loading', false);
            }
        })();
        await this.pendingLoadData;
    }

    protected async loadData(): Promise<void> {
        this.$emit('update:error', null);
        try {
            const validationError = await this.validateConfig();
            if (this.loadDataQueued) {
                return;
            }
            if (validationError) {
                this.handleLoadDataError(validationError);
                return;
            }
            if (this.settings.dataResolver) {
                if (this.settings.enabledDataStreaming) {
                    this.renderWidget = true;
                }
                const fetchDataResult = await this.fetchData();
                if (this.loadDataQueued) {
                    return;
                }
                if (this.isWidgetError(fetchDataResult)) {
                    console.error('error while fetching data result', fetchDataResult);
                    this.renderWidget = false;
                    this.handleLoadDataError(fetchDataResult as WidgetError);
                    return;
                }
                const processDataResult = await this.processData(fetchDataResult as D);
                if (this.loadDataQueued) {
                    return;
                }
                if (this.isWidgetError(processDataResult)) {
                    console.error('error while processing data result', fetchDataResult);
                    this.renderWidget = false;
                    this.handleLoadDataError(processDataResult as WidgetError);
                    return;
                }
            }
            this.renderWidget = true;
        } catch (e: any) {
            console.error('unknown error', e);
            this.renderWidget = false;
            const error = await this.mapToWidgetError(e);
            this.handleLoadDataError(error);
        }
    }

    protected handleLoadDataError(error: WidgetError): void {
        this.$emit('update:error', error);
    }

    private async mapToWidgetError(e: any): Promise<WidgetError> {
        if (this.isWidgetError(e)) {
            // this is already a widget error object
            return e as WidgetError;
        }
        if (e.toString() === 'TypeError: Failed to fetch') {
            return {
                type: WidgetErrorType.Service,
                title: `504 | ${i18n.t('error-data.504.title').toString()}`,
                message: i18n.t('error-data.504.message', { error: 'Failed to fetch' }).toString(),
                err: e,
            };
        }
        const status = e.status || 'unknown';
        const title = i18n.t(`error-data.${status}.title`).toString();
        const message = i18n.t(`error-data.${status}.message`, { error: e }).toString();
        const error: WidgetError = {
            type: WidgetErrorType.Service,
            title: `${e.status || 500} | ${title}`,
            message: message,
            details: undefined,
            err: e,
        };
        // try getting some error details from the service result
        try {
            if (e.clone) {
                const details = await e.clone().text();
                if (details) {
                    error.details = details;
                }
            }
        } catch (ex) {
            // could not get error message
            error.details = e as unknown as string;
        }
        return error;
    }

    private isWidgetError(e: any): boolean {
        if (!e || typeof e !== 'object' || !e.type) {
            return false;
        }
        switch (e.type as WidgetErrorType) {
            case WidgetErrorType.NoDataSource: return true;
            case WidgetErrorType.IncompleteDataSources: return true;
            case WidgetErrorType.NoPermissionForMetric: return true;
            case WidgetErrorType.MissingLicenseFeature: return true;
            case WidgetErrorType.MissingMetrics: return true;
            case WidgetErrorType.InvalidConfig: return true;
            case WidgetErrorType.Service: return true;
            case WidgetErrorType.NoData: return true;
            case WidgetErrorType.Custom: return true;
            default: return false;
        }
    }

    protected async fetchData(): Promise<D|WidgetError> {
        const data = this.settings.dataResolver?.getWidgetData(this.widget);
        if (!data) {
            const error: WidgetError = { type: WidgetErrorType.NoData };
            throw error;
        }
        return data;
    }

    protected processData(data: D): Promise<void|WidgetError> {
        // implemented in derived class
        return Promise.resolve();
    }

    protected async validateConfig(): Promise<null|WidgetError> {
        // implemented in derived class
        return null;
    }
}

export type WidgetComposable<D = any> = {
    widget: Ref<WidgetConfig>;
    settings: Ref<WidgetSettings<D>>;
    renderWidget: Ref<boolean>;
    loadWidget: () => Promise<void>;
    loadData: () => Promise<void>;
    validateConfig: () => Promise<null|WidgetError>;
    processData: (data: D) => Promise<void|WidgetError>;
    handleLoadDataError: (error: WidgetError) => void;
    fetchData: () => Promise<D|WidgetError>;
    getMenuActions: () => MenuAction[];
    mapToWidgetError: (e: any) => Promise<WidgetError>;
    isWidgetError: (e: any) => boolean;
}

export const defaultSettings: WidgetSettings<never> = {
    supportsCompare: false,
    supportsTable: false,
    enableTimeBar: true,
    enabledDataStreaming: false,
    dataResolver: undefined,
};

type WidgetProps = {
    widgetConfig: WidgetConfig,
    expanded?: boolean,
    report?: boolean,
    loading?: boolean
}

type WidgetEmits<D> = {
    (e: 'update:settings', widget: WidgetSettings<D>): void;
    (e: 'update:loading', isLoading: boolean): void;
    (e: 'update:error', error: WidgetError|null): void;
}

async function defaultProcessData(data: any): Promise<void | WidgetError> {
    console.debug('skip processing data', data);
}
async function defaultValidateConfig(): Promise<null | WidgetError> {
    return null;
}
function defaultGetMenuActions(): MenuAction[] {
    return [];
}

export function useWidget<D = any>(
    props: WidgetProps,
    emits: WidgetEmits<D>,
    settingsOverrides?: Partial<WidgetSettings<D>>,
    processData: (data: D) => Promise<void | WidgetError> = defaultProcessData,
    validateConfig: () => Promise<null | WidgetError> = defaultValidateConfig,
    getMenuActions: () => MenuAction[] = defaultGetMenuActions,
): WidgetComposable<D> {
    const renderWidget = ref(true);
    const loadDataQueued = ref(true);
    const pendingLoadData = ref<Promise<void>>();
    const settings = ref<WidgetSettings<D>>({ ...defaultSettings, ...settingsOverrides });
    const onI18nChanged = (event: I18nChangedEvent): void => {
        if (event.type === I18nChangedEventType.LanguageLoaded && event.lang === i18n.locale) {
            loadWidget();
        }
    };
    I18nAdapter.onChange(onI18nChanged);

    const widget = computed(() => Utils.deepCopy(props.widgetConfig));

    function isWidgetError(e: any): boolean {
        return e && typeof e === 'object' && Object.values(WidgetErrorType).includes(e.type);
    }

    async function mapToWidgetError(e: any): Promise<WidgetError> {
        if (isWidgetError(e)) return e;

        if (e.toString() === 'TypeError: Failed to fetch') {
            return {
                type: WidgetErrorType.Service,
                title: `504 | ${i18n.t('error-data.504.title')}`,
                message: i18n.t('error-data.504.message', { error: 'Failed to fetch' }).toString(),
                err: e,
            };
        }

        const status = e.status || 'unknown';
        const error: WidgetError = {
            type: WidgetErrorType.Service,
            title: `${status} | ${i18n.t(`error-data.${status}.title`)}`,
            message: i18n.t(`error-data.${status}.message`, { error: e }).toString(),
            details: undefined,
            err: e,
        };

        try {
            if (e.clone) {
                error.details = await e.clone().text();
            }
        } catch {
            error.details = String(e);
        }

        return error;
    }

    async function fetchData(): Promise<D | WidgetError> {
        const result = settings.value.dataResolver?.getWidgetData(widget.value);
        if (result) return result;

        const widgetError = {
            type: WidgetErrorType.NoData,
        } as WidgetError;

        console.error('no data', widgetError);

        throw widgetError;
    }

    async function loadData(): Promise<void> {
        emits('update:error', null);
        try {
            const validationError = await validateConfig();
            if (loadDataQueued.value) return;

            if (validationError) {
                handleLoadDataError(validationError);
                return;
            }

            if (settings.value.dataResolver) {
                if (settings.value.enabledDataStreaming) {
                    renderWidget.value = true;
                }

                const data = await fetchData();
                if (loadDataQueued.value) return;

                if (isWidgetError(data)) {
                    console.error('error while fetching data', data);
                    renderWidget.value = false;
                    handleLoadDataError(data as WidgetError);
                    return;
                }

                const process = await processData(data as D);
                if (loadDataQueued.value) return;

                if (isWidgetError(process)) {
                    console.error('error while processing data', process);
                    renderWidget.value = false;
                    handleLoadDataError(process as WidgetError);
                    return;
                }
            }

            renderWidget.value = true;
        } catch (e: any) {
            console.error('unknown error', e);
            renderWidget.value = false;
            const err = await mapToWidgetError(e);
            handleLoadDataError(err);
        }
    }

    function handleLoadDataError(error: WidgetError): void {
        emits('update:error', error);
    }
    async function loadWidget(): Promise<void> {
        loadDataQueued.value = true;

        if (pendingLoadData.value) {
            await pendingLoadData.value;
        }

        pendingLoadData.value = (async () => {
            loadDataQueued.value = false;
            emits('update:loading', true);
            await loadData();
            if (!loadDataQueued.value) {
                emits('update:loading', false);
            }
        })();

        await pendingLoadData.value;
    }

    onBeforeMount(() => {
        if (!props.report && widget.value.resolution === Resolution.Min10) {
            setInterval(loadData, 1000 * 60 * 15);
        }
    });
    onMounted(async () => {
        emits('update:settings', settings.value);
        await loadWidget();
    });
    onBeforeUnmount(() => I18nAdapter.offChange(onI18nChanged));

    watch(() => props.expanded, () => {
        renderWidget.value = false;
        setTimeout(() => (renderWidget.value = true), 0);
    });
    watch(() => props.widgetConfig, loadWidget);
    watch(() => i18n.locale, loadWidget);

    return {
        widget,
        settings,
        renderWidget,
        loadWidget,
        loadData,
        validateConfig,
        processData,
        handleLoadDataError,
        fetchData,
        mapToWidgetError,
        isWidgetError,
        getMenuActions,
    };
}
